/**
 * SpiceAssessmentOutput — structured SPICE Assessment Report renderer
 * (Phase X.3.5.4).
 *
 * Composes: report header (DATE/TO/FROM/RE) + salutation + opening + the seven
 * SPICE narrative sections + the three skill-goal blocks (with the Cognitive
 * strengths slot) + the five activity-modality tables + the summary (visit
 * recap + engagement-not-achievement + Let's Engage!) + the sign-off + the
 * follow-up questions block. Read-only; the Save + Download row lives in the
 * AssessmentDrafter page (mirrors AssessmentDraftOutput).
 *
 * No billing block: SPICE is paid by the session, and the SPICE response
 * carries no billing_suggestion at all.
 */

import { type ReactNode } from 'react';
import type {
  SpiceAssessmentReport,
  SpiceReportSections,
  SpiceModalityTables,
} from '@/lib/api';
import { AssessmentFollowUpsBlock } from './AssessmentFollowUpsBlock';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

export interface SpiceAssessmentOutputProps {
  response: SpiceAssessmentReport;
}

const HEADING_STYLE = { color: 'var(--color-sage-800)' } as const;
const SUBHEADING_STYLE = { color: 'var(--color-sage-400)' } as const;
const BODY_STYLE = { color: 'var(--color-ink)' } as const;
const SUBTLE_STYLE = { color: 'var(--color-sage-300)' } as const;

const SECTION_ORDER: Array<[keyof SpiceReportSections, string]> = [
  ['spice_assessment', 'SPICE ASSESSMENT'],
  ['current_social_activity_status', 'CURRENT SOCIAL ACTIVITY STATUS'],
  ['family_educational_vocational_history', 'FAMILY, EDUCATIONAL AND VOCATIONAL HISTORY'],
  ['hobby_special_interest_inventory', 'HOBBY AND SPECIAL INTEREST INVENTORY'],
  ['sensorimotor_assessment', 'SENSORIMOTOR ASSESSMENT'],
  ['physical_assessment', 'PHYSICAL ASSESSMENT'],
  ['memory_cognitive_assessment', 'MEMORY AND COGNITIVE ASSESSMENT'],
];

const MODALITY_ORDER: Array<[keyof SpiceModalityTables, string]> = [
  ['cognitive', 'Cognitive'],
  ['long_term_memory', 'Long Term Memory'],
  ['orientation', 'Orientation'],
  ['physical', 'Physical'],
  ['toe_tappin_memories', "Toe-Tappin' Memories"],
];

function Prose({ children }: { children: string }): ReactNode {
  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap" style={BODY_STYLE}>
      {children}
    </p>
  );
}

function SubHeading({ children }: { children: string }): ReactNode {
  return (
    <h4 className="text-sm font-semibold mt-2" style={SUBHEADING_STYLE}>
      {children}
    </h4>
  );
}

export function SpiceAssessmentOutput({ response }: SpiceAssessmentOutputProps): ReactNode {
  const contact = useSupportContact();
  const { header, salutation, opening, sections, skill_goals, modality_framing, modalities, summary, sign_off, signatory_missing, follow_up_questions } =
    response;

  return (
    <div className="space-y-4" data-testid="spice-assessment-output">
      <section
        className="bg-paper border border-sage-800/[8%] rounded-[12px] p-4 space-y-1"
        aria-label="SPICE assessment report"
        data-testid="spice-report-body"
      >
        {/* Header block */}
        <div
          className="space-y-0.5 text-sm pb-2 mb-2 border-b"
          style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
        >
          <p><span className="font-semibold">DATE:</span> {header.date}</p>
          <p><span className="font-semibold">TO:</span> {header.to}</p>
          <p><span className="font-semibold">FROM:</span> {header.from}</p>
          <p><span className="font-semibold">RE:</span> {header.re}</p>
        </div>

        <Prose>{salutation}</Prose>
        <Prose>{opening}</Prose>

        {SECTION_ORDER.map(([key, title]) => (
          <div key={key}>
            <h3 className="text-sm font-semibold underline mt-4 mb-1" style={HEADING_STYLE}>
              {title}
            </h3>
            <Prose>{sections[key]}</Prose>
          </div>
        ))}

        {/* Activity goals + modalities */}
        <h3 className="text-sm font-semibold underline mt-4 mb-1" style={HEADING_STYLE}>
          ACTIVITY GOALS AND MODALITIES
        </h3>
        <Prose>{skill_goals.program_framing}</Prose>
        <SubHeading>Leisure Skill Goals</SubHeading>
        <Prose>{skill_goals.leisure_skill_goals}</Prose>
        <SubHeading>Cognitive Skill Goals</SubHeading>
        <Prose>{skill_goals.cognitive_skill_goals}</Prose>
        <SubHeading>Strengths</SubHeading>
        <Prose>{skill_goals.cognitive_strengths}</Prose>
        <SubHeading>Physical Skill Goals</SubHeading>
        <Prose>{skill_goals.physical_skill_goals}</Prose>

        <h3 className="text-sm font-semibold underline mt-4 mb-1" style={HEADING_STYLE}>
          RECOMMENDED ACTIVITY MODALITIES
        </h3>
        <Prose>{modality_framing}</Prose>
        {MODALITY_ORDER.map(([key, label]) => (
          <div key={key} className="mt-2" data-testid={`spice-modality-${key}`}>
            <h4 className="text-sm font-semibold" style={SUBHEADING_STYLE}>
              {label}
            </h4>
            <ul className="list-disc pl-5 text-sm leading-relaxed" style={BODY_STYLE}>
              {modalities[key].map((activity, i) => (
                <li key={i}>{activity}</li>
              ))}
            </ul>
          </div>
        ))}

        {/* Summary */}
        <h3 className="text-sm font-semibold underline mt-4 mb-1" style={HEADING_STYLE}>
          SUMMARY AND RECOMMENDATIONS
        </h3>
        <Prose>{summary.visit_rapport_recap}</Prose>
        <Prose>{summary.engagement_not_achievement}</Prose>
        <Prose>{summary.lets_engage}</Prose>

        {/* Sign-off: the server-built signature block (one authenticated
            identity with the FROM line). */}
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap mt-4 pt-2 border-t"
          style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
        >
          {sign_off}
        </p>

        {/* Signature-integrity P0: unresolved author identity. The document
            ends at "Sincerely yours," with NO name; this visible prompt is
            not part of the document body and never reaches the .docx. */}
        {signatory_missing === true && (
          <div
            role="status"
            className="mt-2 rounded-[10px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700"
            data-testid="spice-signatory-missing"
          >
            {contactSentence(
              "This report has no signature because your name isn't on your team profile yet. Ask {contact_admin} to add your name on the Team page, then generate again.",
              contact,
            )}
          </div>
        )}
      </section>

      {/* Follow-up questions (gaps to resolve before finalizing). No billing. */}
      <AssessmentFollowUpsBlock questions={follow_up_questions} />

      <p className="text-xs" style={SUBTLE_STYLE}>
        Review every section before saving. The AI organizes your visit notes into the SPICE Activity Plan; it does not replace your clinical judgment.
      </p>

      {/* Editing happens in Word: the SPICE report keeps its formatting in the
          downloaded .docx, which is the deliverable. */}
      <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-in-word-nudge">
        To make edits, download and finalize in Word.
      </p>
    </div>
  );
}
